import { DemoPreview } from '../board/DemoPreview';
import { color, font } from '../tokens';
import { AccentButton, Button, GhostButton } from '../ui/primitives';
import type { Navigate } from '../lib/router';

/** Marketing page, faithful to the design canvas. */

const STEPS: [string, string, string][] = [
  [
    '01',
    'Drop everything in',
    'Sticky notes, text, images, and link cards. Drag files straight from your desktop onto the board — no folders, no naming, no structure yet.',
  ],
  [
    '02',
    'Group what belongs together',
    'Select a cluster and collapse it into a single labelled chip, so a crowded board reads clean again. Double-click to spill it back out exactly where it was.',
  ],
  [
    '03',
    'Frame areas and zoom out',
    'Draw a named region around each stretch of canvas — moodboard here, copy there, layout mock next door. Whole areas move as one, and zooming out gives you the map.',
  ],
];

const FAQ: [string, string][] = [
  [
    'Is there a limit on board size?',
    'The canvas extends in every direction. Each board supports up to 5,000 items, with separate limits for drawings, connectors, and comments.',
  ],
  [
    'Can I work with other people?',
    'Share a public link, leave pinned comments, and see collaborators’ cursors live on the board.',
  ],
  [
    'What can I put on a board?',
    'Sticky notes, text, images, link cards, freehand drawing, arrows, groups, and framed areas.',
  ],
  [
    'What does it cost?',
    'Free while in beta. Create as many boards as you need, with up to 5,000 items per board. No card needed.',
  ],
];

export function LandingScreen({
  accent,
  navigate,
  signedIn,
}: {
  accent: string;
  navigate: Navigate;
  signedIn: boolean;
}) {
  const eyebrow = { fontFamily: font.mono, fontSize: 11, letterSpacing: '.16em', color: color.textFaint } as const;
  const section = { maxWidth: 820, margin: '0 auto', padding: '100px 32px 0' } as const;

  return (
    <div
      className="sp-landing"
      style={{ height: '100%', overflowY: 'auto', '--sp-accent': accent } as React.CSSProperties}
    >
      <div style={{ minHeight: '100%' }}>
        <header
          style={{
            height: 52,
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            padding: '0 20px',
            borderBottom: `1px solid ${color.line}`,
            background: color.bg,
            position: 'sticky',
            top: 0,
            zIndex: 10,
          }}
        >
          <div style={{ width: 19, height: 19, borderRadius: 6, background: accent }} />
          <div style={{ fontSize: 14.5, fontWeight: 700 }}>Scribbleplot</div>
          <div
            style={{
              fontFamily: font.mono,
              fontSize: 10,
              letterSpacing: '.08em',
              color: color.textFaint,
              border: `1px solid ${color.lineStrong}`,
              borderRadius: 6,
              padding: '2px 7px',
            }}
          >
            BETA
          </div>
          <div style={{ flex: 1 }} />
          {signedIn ? (
            <AccentButton accent={accent} onClick={() => navigate({ name: 'home' })}>
              Open your boards
            </AccentButton>
          ) : (
            <>
              <Button
                onClick={() => navigate({ name: 'signin' })}
                hoverStyle={{ color: color.text }}
                style={{
                  height: 30,
                  padding: '0 13px',
                  borderRadius: 9,
                  border: 'none',
                  background: 'transparent',
                  color: '#B9BCC7',
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                Sign in
              </Button>
              <AccentButton accent={accent} onClick={() => navigate({ name: 'demo' })}>
                Try the demo
              </AccentButton>
            </>
          )}
        </header>

        <div className="sp-landing-hero">
          <div style={{ ...eyebrow, color: accent }}>INFINITE CANVAS · IDEA MAPPING</div>
          <h1
            style={{
              fontSize: 'clamp(40px,5.4vw,62px)',
              fontWeight: 800,
              letterSpacing: '-.03em',
              lineHeight: 1.04,
              margin: '20px 0 0',
            }}
          >
            Every idea. One canvas.
          </h1>
          <p
            style={{
              fontSize: 16.5,
              color: color.textMuted,
              lineHeight: 1.7,
              maxWidth: 560,
              margin: '20px auto 0',
              textWrap: 'pretty',
            }}
          >
            Notes, images, and rough layouts on one zoomable board. Drop everything in, group what belongs together, and
            mock the layout right beside the inspiration.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center', marginTop: 30 }}>
            <AccentButton accent={accent} size="lg" onClick={() => navigate({ name: signedIn ? 'home' : 'demo' })}>
              {signedIn ? 'Open your boards' : 'Try the demo'}
            </AccentButton>
            <GhostButton size="lg" onClick={() => navigate({ name: 'signup' })}>
              Create an account
            </GhostButton>
          </div>
        </div>

        <div style={{ maxWidth: 1100, margin: '56px auto 0', padding: '0 20px' }}>
          <DemoPreview />
        </div>

        <section style={section}>
          <div style={eyebrow}>HOW IT WORKS</div>
          <div style={{ marginTop: 28, display: 'flex', flexDirection: 'column' }}>
            {STEPS.map(([num, title, body]) => (
              <div
                key={num}
                style={{ display: 'flex', gap: 26, padding: '26px 0', borderTop: `1px solid ${color.lineSoft}` }}
              >
                <div style={{ fontFamily: font.mono, fontSize: 12, color: accent, paddingTop: 3, flex: 'none' }}>
                  {num}
                </div>
                <div>
                  <div style={{ fontSize: 19, fontWeight: 700, letterSpacing: '-.015em' }}>{title}</div>
                  <div style={{ fontSize: 14, color: color.textMuted, lineHeight: 1.65, marginTop: 7, maxWidth: 560 }}>
                    {body}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section style={section}>
          <div style={eyebrow}>WHAT PEOPLE MAP HERE</div>
          <p
            style={{
              fontSize: 16,
              color: color.textMuted,
              lineHeight: 1.8,
              marginTop: 20,
              maxWidth: 620,
              textWrap: 'pretty',
            }}
          >
            Website design, with the moodboard and the layout mock side by side. Brand direction, where palettes and
            type specimens sit together until one wins. Product thinking, with screens joined by arrows so the flow
            draws itself. And plain asset libraries — every logo, shot, and export grouped into chips you can actually
            find.
          </p>
        </section>

        <section style={section}>
          <div style={eyebrow}>QUESTIONS</div>
          <div style={{ marginTop: 28 }}>
            {FAQ.map(([question, answer]) => (
              <div key={question} style={{ padding: '22px 0', borderTop: `1px solid ${color.lineSoft}` }}>
                <div style={{ fontSize: 15, fontWeight: 700 }}>{question}</div>
                <div style={{ fontSize: 14, color: color.textMuted, lineHeight: 1.65, marginTop: 6 }}>{answer}</div>
              </div>
            ))}
          </div>
        </section>

        <section style={{ maxWidth: 820, margin: '0 auto', padding: '100px 32px 110px', textAlign: 'center' }}>
          <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-.02em' }}>Ready to map it out?</div>
          <div style={{ fontSize: 14.5, color: color.textMuted, marginTop: 8 }}>
            Free while in beta. Your first board takes 30 seconds.
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 24, flexWrap: 'wrap' }}>
            <AccentButton accent={accent} size="lg" onClick={() => navigate({ name: signedIn ? 'home' : 'demo' })}>
              {signedIn ? 'Open your boards' : 'Try the demo'}
            </AccentButton>
            <GhostButton size="lg" onClick={() => navigate({ name: 'signup' })}>
              Create an account
            </GhostButton>
          </div>
        </section>

        <footer style={{ borderTop: `1px solid ${color.line}` }}>
          <div
            style={{
              maxWidth: 1120,
              margin: '0 auto',
              padding: '24px 36px',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexWrap: 'wrap',
            }}
          >
            <div style={{ width: 16, height: 16, borderRadius: 5, background: accent }} />
            <div style={{ fontSize: 13.5, fontWeight: 700 }}>Scribbleplot</div>
            <div style={{ fontSize: 12, color: color.textFaint }}>© {new Date().getFullYear()} scribbleplot.com</div>
            <div style={{ flex: 1 }} />
            <a href="/privacy" style={{ fontSize: 12, color: color.textMuted }}>
              Privacy
            </a>
          </div>
        </footer>
      </div>
    </div>
  );
}
